/*! *****************************************************************************
Copyright (C) Microsoft. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
import { __values } from "tslib";
var hasOwn = Object.prototype.hasOwnProperty;
// feature test for Symbol support
var supportsSymbol = typeof Symbol === "function";
var toPrimitiveSymbol = supportsSymbol && typeof Symbol.toPrimitive !== "undefined" ? Symbol.toPrimitive : "@@toPrimitive";
var iteratorSymbol = supportsSymbol && typeof Symbol.iterator !== "undefined" ? Symbol.iterator : "@@iterator";
var supportsCreate = typeof Object.create === "function"; // feature test for Object.create support
var supportsProto = { __proto__: [] } instanceof Array; // feature test for __proto__ support
var downLevel = !supportsCreate && !supportsProto;
var HashMap = {
    // create an object in dictionary mode (a.k.a. "slow" mode in v8)
    create: supportsCreate
        ? function () { return MakeDictionary(Object.create(null)); }
        : supportsProto
            ? function () { return MakeDictionary({ __proto__: null }); }
            : function () { return MakeDictionary({}); },
    has: downLevel
        ? function (map, key) { return hasOwn.call(map, key); }
        : function (map, key) { return key in map; },
    get: downLevel
        ? function (map, key) { return hasOwn.call(map, key) ? map[key] : undefined; }
        : function (map, key) { return map[key]; },
};
// Load global or shim versions of Map, Set, and WeakMap
var functionPrototype = Object.getPrototypeOf(Function);
var usePolyfill = typeof process === "object" && process.env && process.env["REFLECT_METADATA_USE_MAP_POLYFILL"] === "true";
var _Map = !usePolyfill && typeof Map === "function" && typeof Map.prototype.entries === "function" ? Map : CreateMapPolyfill();
var _Set = !usePolyfill && typeof Set === "function" && typeof Set.prototype.entries === "function" ? Set : CreateSetPolyfill();
var _WeakMap = !usePolyfill && typeof WeakMap === "function" ? WeakMap : CreateWeakMapPolyfill();
// [[Metadata]] internal slot
// https://rbuckton.github.io/reflect-metadata/#ordinary-object-internal-methods-and-internal-slots
var Metadata = new _WeakMap();
/**
 * Applies a set of decorators to a property of a target object.
 * @param decorators An array of decorators.
 * @param target The target object.
 * @param propertyKey (Optional) The property key to decorate.
 * @param attributes (Optional) The property descriptor for the target key.
 * @remarks Decorators are applied in reverse order.
 * @example
 *
 *     class Example {
 *         // property declarations are not part of ES6, though they are valid in TypeScript:
 *         // static staticProperty;
 *         // property;
 *
 *         constructor(p) { }
 *         static staticMethod(p) { }
 *         method(p) { }
 *     }
 *
 *     // constructor
 *     Example = Reflect.decorate(decoratorsArray, Example);
 *
 *     // property (on constructor)
 *     Reflect.decorate(decoratorsArray, Example, "staticProperty");
 *
 *     // property (on prototype)
 *     Reflect.decorate(decoratorsArray, Example.prototype, "property");
 *
 *     // method (on constructor)
 *     Object.defineProperty(Example, "staticMethod",
 *         Reflect.decorate(decoratorsArray, Example, "staticMethod",
 *             Object.getOwnPropertyDescriptor(Example, "staticMethod")));
 *
 *     // method (on prototype)
 *     Object.defineProperty(Example.prototype, "method",
 *         Reflect.decorate(decoratorsArray, Example.prototype, "method",
 *             Object.getOwnPropertyDescriptor(Example.prototype, "method")));
 *
 */
function decorate(decorators, target, propertyKey, attributes) {
    if (!IsUndefined(propertyKey)) {
        if (!IsArray(decorators))
            throw new TypeError();
        if (!IsObject(target))
            throw new TypeError();
        if (!IsObject(attributes) && !IsUndefined(attributes) && !IsNull(attributes))
            throw new TypeError();
        if (IsNull(attributes))
            attributes = undefined;
        propertyKey = ToPropertyKey(propertyKey);
        return DecorateProperty(decorators, target, propertyKey, attributes);
    }
    else {
        if (!IsArray(decorators))
            throw new TypeError();
        if (!IsConstructor(target))
            throw new TypeError();
        return DecorateConstructor(decorators, target);
    }
}
// 4.1.2 Reflect.metadata(metadataKey, metadataValue)
// https://rbuckton.github.io/reflect-metadata/#reflect.metadata
/**
  * A default metadata decorator factory that can be used on a class, class member, or parameter.
  * @param metadataKey The key for the metadata entry.
  * @param metadataValue The value for the metadata entry.
  * @returns A decorator function.
  * @remarks
  * If `metadataKey` is already defined for the target and target key, the
  * metadataValue for that key will be overwritten.
  * @example
  *
  *     // constructor
  *     @Reflect.metadata(key, value)
  *     class Example {
  *     }
  *
  *     // property (on constructor, TypeScript only)
  *     class Example {
  *         @Reflect.metadata(key, value)
  *         static staticProperty;
  *     }
  *
  *     // property (on prototype, TypeScript only)
  *     class Example {
  *         @Reflect.metadata(key, value)
  *         property;
  *     }
  *
  *     // method (on constructor)
  *     class Example {
  *         @Reflect.metadata(key, value)
  *         static staticMethod() { }
  *     }
  *
  *     // method (on prototype)
  *     class Example {
  *         @Reflect.metadata(key, value)
  *         method() { }
  *     }
  *
  */
function metadata(metadataKey, metadataValue) {
    function decorator(target, propertyKey) {
        if (!IsObject(target))
            throw new TypeError();
        if (!IsUndefined(propertyKey) && !IsPropertyKey(propertyKey))
            throw new TypeError();
        OrdinaryDefineOwnMetadata(metadataKey, metadataValue, target, propertyKey);
    }
    return decorator;
}
/**
 * Define a unique metadata entry on the target.
 * @param metadataKey A key used to store and retrieve metadata.
 * @param metadataValue A value that contains attached metadata.
 * @param target The target object on which to define metadata.
 * @param propertyKey (Optional) The property key for the target.
 * @example
 *
 *     class Example {
 *         // property declarations are not part of ES6, though they are valid in TypeScript:
 *         // static staticProperty;
 *         // property;
 *
 *         constructor(p) { }
 *         static staticMethod(p) { }
 *         method(p) { }
 *     }
 *
 *     // constructor
 *     Reflect.defineMetadata("custom:annotation", options, Example);
 *
 *     // property (on constructor)
 *     Reflect.defineMetadata("custom:annotation", options, Example, "staticProperty");
 *
 *     // property (on prototype)
 *     Reflect.defineMetadata("custom:annotation", options, Example.prototype, "property");
 *
 *     // method (on constructor)
 *     Reflect.defineMetadata("custom:annotation", options, Example, "staticMethod");
 *
 *     // method (on prototype)
 *     Reflect.defineMetadata("custom:annotation", options, Example.prototype, "method");
 *
 *     // decorator factory as metadata-producing annotation.
 *     function MyAnnotation(options): Decorator {
 *         return (target, key?) => Reflect.defineMetadata("custom:annotation", options, target, key);
 *     }
 *
 */
function defineMetadata(metadataKey, metadataValue, target, propertyKey) {
    if (!IsObject(target))
        throw new TypeError();
    if (!IsUndefined(propertyKey))
        propertyKey = ToPropertyKey(propertyKey);
    return OrdinaryDefineOwnMetadata(metadataKey, metadataValue, target, propertyKey);
}
/**
 * Gets a value indicating whether the target object or its prototype chain has the provided metadata key defined.
 * @param metadataKey A key used to store and retrieve metadata.
 * @param target The target object on which the metadata is defined.
 * @param propertyKey (Optional) The property key for the target.
 * @returns `true` if the metadata key was defined on the target object or its prototype chain; otherwise, `false`.
 * @example
 *
 *     class Example {
 *         // property declarations are not part of ES6, though they are valid in TypeScript:
 *         // static staticProperty;
 *         // property;
 *
 *         constructor(p) { }
 *         static staticMethod(p) { }
 *         method(p) { }
 *     }
 *
 *     // constructor
 *     result = Reflect.hasMetadata("custom:annotation", Example);
 *
 *     // property (on constructor)
 *     result = Reflect.hasMetadata("custom:annotation", Example, "staticProperty");
 *
 *     // property (on prototype)
 *     result = Reflect.hasMetadata("custom:annotation", Example.prototype, "property");
 *
 *     // method (on constructor)
 *     result = Reflect.hasMetadata("custom:annotation", Example, "staticMethod");
 *
 *     // method (on prototype)
 *     result = Reflect.hasMetadata("custom:annotation", Example.prototype, "method");
 *
 */
function hasMetadata(metadataKey, target, propertyKey) {
    if (!IsObject(target))
        throw new TypeError();
    if (!IsUndefined(propertyKey))
        propertyKey = ToPropertyKey(propertyKey);
    return OrdinaryHasMetadata(metadataKey, target, propertyKey);
}
/**
 * Gets a value indicating whether the target object has the provided metadata key defined.
 * @param metadataKey A key used to store and retrieve metadata.
 * @param target The target object on which the metadata is defined.
 * @param propertyKey (Optional) The property key for the target.
 * @returns `true` if the metadata key was defined on the target object; otherwise, `false`.
 * @example
 *
 *     class Example {
 *         // property declarations are not part of ES6, though they are valid in TypeScript:
 *         // static staticProperty;
 *         // property;
 *
 *         constructor(p) { }
 *         static staticMethod(p) { }
 *         method(p) { }
 *     }
 *
 *     // constructor
 *     result = Reflect.hasOwnMetadata("custom:annotation", Example);
 *
 *     // property (on constructor)
 *     result = Reflect.hasOwnMetadata("custom:annotation", Example, "staticProperty");
 *
 *     // property (on prototype)
 *     result = Reflect.hasOwnMetadata("custom:annotation", Example.prototype, "property");
 *
 *     // method (on constructor)
 *     result = Reflect.hasOwnMetadata("custom:annotation", Example, "staticMethod");
 *
 *     // method (on prototype)
 *     result = Reflect.hasOwnMetadata("custom:annotation", Example.prototype, "method");
 *
 */
function hasOwnMetadata(metadataKey, target, propertyKey) {
    if (!IsObject(target))
        throw new TypeError();
    if (!IsUndefined(propertyKey))
        propertyKey = ToPropertyKey(propertyKey);
    return OrdinaryHasOwnMetadata(metadataKey, target, propertyKey);
}
/**
 * Gets the metadata value for the provided metadata key on the target object or its prototype chain.
 * @param metadataKey A key used to store and retrieve metadata.
 * @param target The target object on which the metadata is defined.
 * @param propertyKey (Optional) The property key for the target.
 * @returns The metadata value for the metadata key if found; otherwise, `undefined`.
 * @example
 *
 *     class Example {
 *         // property declarations are not part of ES6, though they are valid in TypeScript:
 *         // static staticProperty;
 *         // property;
 *
 *         constructor(p) { }
 *         static staticMethod(p) { }
 *         method(p) { }
 *     }
 *
 *     // constructor
 *     result = Reflect.getMetadata("custom:annotation", Example);
 *
 *     // property (on constructor)
 *     result = Reflect.getMetadata("custom:annotation", Example, "staticProperty");
 *
 *     // property (on prototype)
 *     result = Reflect.getMetadata("custom:annotation", Example.prototype, "property");
 *
 *     // method (on constructor)
 *     result = Reflect.getMetadata("custom:annotation", Example, "staticMethod");
 *
 *     // method (on prototype)
 *     result = Reflect.getMetadata("custom:annotation", Example.prototype, "method");
 *
 */
function getMetadata(metadataKey, target, propertyKey) {
    if (!IsObject(target))
        throw new TypeError();
    if (!IsUndefined(propertyKey))
        propertyKey = ToPropertyKey(propertyKey);
    return OrdinaryGetMetadata(metadataKey, target, propertyKey);
}
/**
 * Gets the metadata value for the provided metadata key on the target object.
 * @param metadataKey A key used to store and retrieve metadata.
 * @param target The target object on which the metadata is defined.
 * @param propertyKey (Optional) The property key for the target.
 * @returns The metadata value for the metadata key if found; otherwise, `undefined`.
 * @example
 *
 *     class Example {
 *         // property declarations are not part of ES6, though they are valid in TypeScript:
 *         // static staticProperty;
 *         // property;
 *
 *         constructor(p) { }
 *         static staticMethod(p) { }
 *         method(p) { }
 *     }
 *
 *     // constructor
 *     result = Reflect.getOwnMetadata("custom:annotation", Example);
 *
 *     // property (on constructor)
 *     result = Reflect.getOwnMetadata("custom:annotation", Example, "staticProperty");
 *
 *     // property (on prototype)
 *     result = Reflect.getOwnMetadata("custom:annotation", Example.prototype, "property");
 *
 *     // method (on constructor)
 *     result = Reflect.getOwnMetadata("custom:annotation", Example, "staticMethod");
 *
 *     // method (on prototype)
 *     result = Reflect.getOwnMetadata("custom:annotation", Example.prototype, "method");
 *
 */
function getOwnMetadata(metadataKey, target, propertyKey) {
    if (!IsObject(target))
        throw new TypeError();
    if (!IsUndefined(propertyKey))
        propertyKey = ToPropertyKey(propertyKey);
    return OrdinaryGetOwnMetadata(metadataKey, target, propertyKey);
}
/**
 * Gets the metadata keys defined on the target object or its prototype chain.
 * @param target The target object on which the metadata is defined.
 * @param propertyKey (Optional) The property key for the target.
 * @returns An array of unique metadata keys.
 * @example
 *
 *     class Example {
 *         // property declarations are not part of ES6, though they are valid in TypeScript:
 *         // static staticProperty;
 *         // property;
 *
 *         constructor(p) { }
 *         static staticMethod(p) { }
 *         method(p) { }
 *     }
 *
 *     // constructor
 *     result = Reflect.getMetadataKeys(Example);
 *
 *     // property (on constructor)
 *     result = Reflect.getMetadataKeys(Example, "staticProperty");
 *
 *     // property (on prototype)
 *     result = Reflect.getMetadataKeys(Example.prototype, "property");
 *
 *     // method (on constructor)
 *     result = Reflect.getMetadataKeys(Example, "staticMethod");
 *
 *     // method (on prototype)
 *     result = Reflect.getMetadataKeys(Example.prototype, "method");
 *
 */
function getMetadataKeys(target, propertyKey) {
    if (!IsObject(target))
        throw new TypeError();
    if (!IsUndefined(propertyKey))
        propertyKey = ToPropertyKey(propertyKey);
    return OrdinaryMetadataKeys(target, propertyKey);
}
/**
 * Gets the unique metadata keys defined on the target object.
 * @param target The target object on which the metadata is defined.
 * @param propertyKey (Optional) The property key for the target.
 * @returns An array of unique metadata keys.
 * @example
 *
 *     class Example {
 *         // property declarations are not part of ES6, though they are valid in TypeScript:
 *         // static staticProperty;
 *         // property;
 *
 *         constructor(p) { }
 *         static staticMethod(p) { }
 *         method(p) { }
 *     }
 *
 *     // constructor
 *     result = Reflect.getOwnMetadataKeys(Example);
 *
 *     // property (on constructor)
 *     result = Reflect.getOwnMetadataKeys(Example, "staticProperty");
 *
 *     // property (on prototype)
 *     result = Reflect.getOwnMetadataKeys(Example.prototype, "property");
 *
 *     // method (on constructor)
 *     result = Reflect.getOwnMetadataKeys(Example, "staticMethod");
 *
 *     // method (on prototype)
 *     result = Reflect.getOwnMetadataKeys(Example.prototype, "method");
 *
 */
function getOwnMetadataKeys(target, propertyKey) {
    if (!IsObject(target))
        throw new TypeError();
    if (!IsUndefined(propertyKey))
        propertyKey = ToPropertyKey(propertyKey);
    return OrdinaryOwnMetadataKeys(target, propertyKey);
}
;
/**
 * Deletes the metadata entry from the target object with the provided key.
 * @param metadataKey A key used to store and retrieve metadata.
 * @param target The target object on which the metadata is defined.
 * @param propertyKey (Optional) The property key for the target.
 * @returns `true` if the metadata entry was found and deleted; otherwise, false.
 * @example
 *
 *     class Example {
 *         // property declarations are not part of ES6, though they are valid in TypeScript:
 *         // static staticProperty;
 *         // property;
 *
 *         constructor(p) { }
 *         static staticMethod(p) { }
 *         method(p) { }
 *     }
 *
 *     // constructor
 *     result = Reflect.deleteMetadata("custom:annotation", Example);
 *
 *     // property (on constructor)
 *     result = Reflect.deleteMetadata("custom:annotation", Example, "staticProperty");
 *
 *     // property (on prototype)
 *     result = Reflect.deleteMetadata("custom:annotation", Example.prototype, "property");
 *
 *     // method (on constructor)
 *     result = Reflect.deleteMetadata("custom:annotation", Example, "staticMethod");
 *
 *     // method (on prototype)
 *     result = Reflect.deleteMetadata("custom:annotation", Example.prototype, "method");
 *
 */
function deleteMetadata(metadataKey, target, propertyKey) {
    if (!IsObject(target))
        throw new TypeError();
    if (!IsUndefined(propertyKey))
        propertyKey = ToPropertyKey(propertyKey);
    var metadataMap = GetOrCreateMetadataMap(target, propertyKey, /*Create*/ false);
    if (IsUndefined(metadataMap))
        return false;
    if (!metadataMap.delete(metadataKey))
        return false;
    if (metadataMap.size > 0)
        return true;
    var targetMetadata = Metadata.get(target);
    targetMetadata.delete(propertyKey);
    if (targetMetadata.size > 0)
        return true;
    Metadata.delete(target);
    return true;
}
function DecorateConstructor(decorators, target) {
    for (var i = decorators.length - 1; i >= 0; --i) {
        var decorator = decorators[i];
        var decorated = decorator(target);
        if (!IsUndefined(decorated) && !IsNull(decorated)) {
            if (!IsConstructor(decorated))
                throw new TypeError();
            target = decorated;
        }
    }
    return target;
}
function DecorateProperty(decorators, target, propertyKey, descriptor) {
    for (var i = decorators.length - 1; i >= 0; --i) {
        var decorator = decorators[i];
        var decorated = decorator(target, propertyKey, descriptor);
        if (!IsUndefined(decorated) && !IsNull(decorated)) {
            if (!IsObject(decorated))
                throw new TypeError();
            descriptor = decorated;
        }
    }
    return descriptor;
}
function GetOrCreateMetadataMap(O, P, Create) {
    var targetMetadata = Metadata.get(O);
    if (IsUndefined(targetMetadata)) {
        if (!Create)
            return undefined;
        targetMetadata = new _Map();
        Metadata.set(O, targetMetadata);
    }
    var metadataMap = targetMetadata.get(P);
    if (IsUndefined(metadataMap)) {
        if (!Create)
            return undefined;
        metadataMap = new _Map();
        targetMetadata.set(P, metadataMap);
    }
    return metadataMap;
}
// 3.1.1.1 OrdinaryHasMetadata(MetadataKey, O, P)
// https://rbuckton.github.io/reflect-metadata/#ordinaryhasmetadata
function OrdinaryHasMetadata(MetadataKey, O, P) {
    var hasOwn = OrdinaryHasOwnMetadata(MetadataKey, O, P);
    if (hasOwn)
        return true;
    var parent = OrdinaryGetPrototypeOf(O);
    if (!IsNull(parent))
        return OrdinaryHasMetadata(MetadataKey, parent, P);
    return false;
}
// 3.1.2.1 OrdinaryHasOwnMetadata(MetadataKey, O, P)
// https://rbuckton.github.io/reflect-metadata/#ordinaryhasownmetadata
function OrdinaryHasOwnMetadata(MetadataKey, O, P) {
    var metadataMap = GetOrCreateMetadataMap(O, P, /*Create*/ false);
    if (IsUndefined(metadataMap))
        return false;
    return ToBoolean(metadataMap.has(MetadataKey));
}
// 3.1.3.1 OrdinaryGetMetadata(MetadataKey, O, P)
// https://rbuckton.github.io/reflect-metadata/#ordinarygetmetadata
function OrdinaryGetMetadata(MetadataKey, O, P) {
    var hasOwn = OrdinaryHasOwnMetadata(MetadataKey, O, P);
    if (hasOwn)
        return OrdinaryGetOwnMetadata(MetadataKey, O, P);
    var parent = OrdinaryGetPrototypeOf(O);
    if (!IsNull(parent))
        return OrdinaryGetMetadata(MetadataKey, parent, P);
    return undefined;
}
// 3.1.4.1 OrdinaryGetOwnMetadata(MetadataKey, O, P)
// https://rbuckton.github.io/reflect-metadata/#ordinarygetownmetadata
function OrdinaryGetOwnMetadata(MetadataKey, O, P) {
    var metadataMap = GetOrCreateMetadataMap(O, P, /*Create*/ false);
    if (IsUndefined(metadataMap))
        return undefined;
    return metadataMap.get(MetadataKey);
}
// 3.1.5.1 OrdinaryDefineOwnMetadata(MetadataKey, MetadataValue, O, P)
// https://rbuckton.github.io/reflect-metadata/#ordinarydefineownmetadata
function OrdinaryDefineOwnMetadata(MetadataKey, MetadataValue, O, P) {
    var metadataMap = GetOrCreateMetadataMap(O, P, /*Create*/ true);
    metadataMap.set(MetadataKey, MetadataValue);
}
// 3.1.6.1 OrdinaryMetadataKeys(O, P)
// https://rbuckton.github.io/reflect-metadata/#ordinarymetadatakeys
function OrdinaryMetadataKeys(O, P) {
    var e_1, _a, e_2, _b;
    var ownKeys = OrdinaryOwnMetadataKeys(O, P);
    var parent = OrdinaryGetPrototypeOf(O);
    if (parent === null)
        return ownKeys;
    var parentKeys = OrdinaryMetadataKeys(parent, P);
    if (parentKeys.length <= 0)
        return ownKeys;
    if (ownKeys.length <= 0)
        return parentKeys;
    var set = new _Set();
    var keys = [];
    try {
        for (var ownKeys_1 = __values(ownKeys), ownKeys_1_1 = ownKeys_1.next(); !ownKeys_1_1.done; ownKeys_1_1 = ownKeys_1.next()) {
            var key = ownKeys_1_1.value;
            var hasKey = set.has(key);
            if (!hasKey) {
                set.add(key);
                keys.push(key);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (ownKeys_1_1 && !ownKeys_1_1.done && (_a = ownKeys_1.return)) _a.call(ownKeys_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    try {
        for (var parentKeys_1 = __values(parentKeys), parentKeys_1_1 = parentKeys_1.next(); !parentKeys_1_1.done; parentKeys_1_1 = parentKeys_1.next()) {
            var key = parentKeys_1_1.value;
            var hasKey = set.has(key);
            if (!hasKey) {
                set.add(key);
                keys.push(key);
            }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (parentKeys_1_1 && !parentKeys_1_1.done && (_b = parentKeys_1.return)) _b.call(parentKeys_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return keys;
}
// 3.1.7.1 OrdinaryOwnMetadataKeys(O, P)
// https://rbuckton.github.io/reflect-metadata/#ordinaryownmetadatakeys
function OrdinaryOwnMetadataKeys(O, P) {
    var keys = [];
    var metadataMap = GetOrCreateMetadataMap(O, P, /*Create*/ false);
    if (IsUndefined(metadataMap))
        return keys;
    var keysObj = metadataMap.keys();
    var iterator = GetIterator(keysObj);
    var k = 0;
    while (true) {
        var next = IteratorStep(iterator);
        if (!next) {
            keys.length = k;
            return keys;
        }
        var nextValue = IteratorValue(next);
        try {
            keys[k] = nextValue;
        }
        catch (e) {
            try {
                IteratorClose(iterator);
            }
            finally {
                throw e;
            }
        }
        k++;
    }
}
// 6 ECMAScript Data Typ0es and Values
// https://tc39.github.io/ecma262/#sec-ecmascript-data-types-and-values
function Type(x) {
    if (x === null)
        return 1 /* Null */;
    switch (typeof x) {
        case "undefined": return 0 /* Undefined */;
        case "boolean": return 2 /* Boolean */;
        case "string": return 3 /* String */;
        case "symbol": return 4 /* Symbol */;
        case "number": return 5 /* Number */;
        case "object": return x === null ? 1 /* Null */ : 6 /* Object */;
        default: return 6 /* Object */;
    }
}
// 6.1 ECMAScript Language Types
// https://tc39.github.io/ecma262/#sec-ecmascript-language-types
var Tag;
(function (Tag) {
    Tag[Tag["Undefined"] = 0] = "Undefined";
    Tag[Tag["Null"] = 1] = "Null";
    Tag[Tag["Boolean"] = 2] = "Boolean";
    Tag[Tag["String"] = 3] = "String";
    Tag[Tag["Symbol"] = 4] = "Symbol";
    Tag[Tag["Number"] = 5] = "Number";
    Tag[Tag["Object"] = 6] = "Object";
})(Tag || (Tag = {}));
// 6.1.1 The Undefined Type
// https://tc39.github.io/ecma262/#sec-ecmascript-language-types-undefined-type
function IsUndefined(x) {
    return x === undefined;
}
// 6.1.2 The Null Type
// https://tc39.github.io/ecma262/#sec-ecmascript-language-types-null-type
function IsNull(x) {
    return x === null;
}
// 6.1.5 The Symbol Type
// https://tc39.github.io/ecma262/#sec-ecmascript-language-types-symbol-type
function IsSymbol(x) {
    return typeof x === "symbol";
}
// 6.1.7 The Object Type
// https://tc39.github.io/ecma262/#sec-object-type
function IsObject(x) {
    return typeof x === "object" ? x !== null : typeof x === "function";
}
// 7.1 Type Conversion
// https://tc39.github.io/ecma262/#sec-type-conversion
// 7.1.1 ToPrimitive(input [, PreferredType])
// https://tc39.github.io/ecma262/#sec-toprimitive
function ToPrimitive(input, PreferredType) {
    switch (Type(input)) {
        case 0 /* Undefined */: return input;
        case 1 /* Null */: return input;
        case 2 /* Boolean */: return input;
        case 3 /* String */: return input;
        case 4 /* Symbol */: return input;
        case 5 /* Number */: return input;
    }
    var hint = PreferredType === 3 /* String */ ? "string" : PreferredType === 5 /* Number */ ? "number" : "default";
    var exoticToPrim = GetMethod(input, toPrimitiveSymbol);
    if (exoticToPrim !== undefined) {
        var result = exoticToPrim.call(input, hint);
        if (IsObject(result))
            throw new TypeError();
        return result;
    }
    return OrdinaryToPrimitive(input, hint === "default" ? "number" : hint);
}
// 7.1.1.1 OrdinaryToPrimitive(O, hint)
// https://tc39.github.io/ecma262/#sec-ordinarytoprimitive
function OrdinaryToPrimitive(O, hint) {
    if (hint === "string") {
        var toString = O.toString;
        if (IsCallable(toString)) {
            var result = toString.call(O);
            if (!IsObject(result))
                return result;
        }
        var valueOf = O.valueOf;
        if (IsCallable(valueOf)) {
            var result = valueOf.call(O);
            if (!IsObject(result))
                return result;
        }
    }
    else {
        var valueOf = O.valueOf;
        if (IsCallable(valueOf)) {
            var result = valueOf.call(O);
            if (!IsObject(result))
                return result;
        }
        var toString = O.toString;
        if (IsCallable(toString)) {
            var result = toString.call(O);
            if (!IsObject(result))
                return result;
        }
    }
    throw new TypeError();
}
// 7.1.2 ToBoolean(argument)
// https://tc39.github.io/ecma262/2016/#sec-toboolean
function ToBoolean(argument) {
    return !!argument;
}
// 7.1.12 ToString(argument)
// https://tc39.github.io/ecma262/#sec-tostring
function ToString(argument) {
    return "" + argument;
}
// 7.1.14 ToPropertyKey(argument)
// https://tc39.github.io/ecma262/#sec-topropertykey
function ToPropertyKey(argument) {
    var key = ToPrimitive(argument, 3 /* String */);
    if (IsSymbol(key))
        return key;
    return ToString(key);
}
// 7.2 Testing and Comparison Operations
// https://tc39.github.io/ecma262/#sec-testing-and-comparison-operations
// 7.2.2 IsArray(argument)
// https://tc39.github.io/ecma262/#sec-isarray
function IsArray(argument) {
    return Array.isArray
        ? Array.isArray(argument)
        : argument instanceof Object
            ? argument instanceof Array
            : Object.prototype.toString.call(argument) === "[object Array]";
}
// 7.2.3 IsCallable(argument)
// https://tc39.github.io/ecma262/#sec-iscallable
function IsCallable(argument) {
    // NOTE: This is an approximation as we cannot check for [[Call]] internal method.
    return typeof argument === "function";
}
// 7.2.4 IsConstructor(argument)
// https://tc39.github.io/ecma262/#sec-isconstructor
function IsConstructor(argument) {
    // NOTE: This is an approximation as we cannot check for [[Construct]] internal method.
    return typeof argument === "function";
}
// 7.2.7 IsPropertyKey(argument)
// https://tc39.github.io/ecma262/#sec-ispropertykey
function IsPropertyKey(argument) {
    switch (Type(argument)) {
        case 3 /* String */: return true;
        case 4 /* Symbol */: return true;
        default: return false;
    }
}
// 7.3 Operations on Objects
// https://tc39.github.io/ecma262/#sec-operations-on-objects
// 7.3.9 GetMethod(V, P)
// https://tc39.github.io/ecma262/#sec-getmethod
function GetMethod(V, P) {
    var func = V[P];
    if (func === undefined || func === null)
        return undefined;
    if (!IsCallable(func))
        throw new TypeError();
    return func;
}
// 7.4 Operations on Iterator Objects
// https://tc39.github.io/ecma262/#sec-operations-on-iterator-objects
function GetIterator(obj) {
    var method = GetMethod(obj, iteratorSymbol);
    if (!IsCallable(method))
        throw new TypeError(); // from Call
    var iterator = method.call(obj);
    if (!IsObject(iterator))
        throw new TypeError();
    return iterator;
}
// 7.4.4 IteratorValue(iterResult)
// https://tc39.github.io/ecma262/2016/#sec-iteratorvalue
function IteratorValue(iterResult) {
    return iterResult.value;
}
// 7.4.5 IteratorStep(iterator)
// https://tc39.github.io/ecma262/#sec-iteratorstep
function IteratorStep(iterator) {
    var result = iterator.next();
    return result.done ? false : result;
}
// 7.4.6 IteratorClose(iterator, completion)
// https://tc39.github.io/ecma262/#sec-iteratorclose
function IteratorClose(iterator) {
    var f = iterator["return"];
    if (f)
        f.call(iterator);
}
// 9.1 Ordinary Object Internal Methods and Internal Slots
// https://tc39.github.io/ecma262/#sec-ordinary-object-internal-methods-and-internal-slots
// 9.1.1.1 OrdinaryGetPrototypeOf(O)
// https://tc39.github.io/ecma262/#sec-ordinarygetprototypeof
function OrdinaryGetPrototypeOf(O) {
    var proto = Object.getPrototypeOf(O);
    if (typeof O !== "function" || O === functionPrototype)
        return proto;
    // TypeScript doesn't set __proto__ in ES5, as it's non-standard.
    // Try to determine the superclass constructor. Compatible implementations
    // must either set __proto__ on a subclass constructor to the superclass constructor,
    // or ensure each class has a valid `constructor` property on its prototype that
    // points back to the constructor.
    // If this is not the same as Function.[[Prototype]], then this is definately inherited.
    // This is the case when in ES6 or when using __proto__ in a compatible browser.
    if (proto !== functionPrototype)
        return proto;
    // If the super prototype is Object.prototype, null, or undefined, then we cannot determine the heritage.
    var prototype = O.prototype;
    var prototypeProto = prototype && Object.getPrototypeOf(prototype);
    if (prototypeProto == null || prototypeProto === Object.prototype)
        return proto;
    // If the constructor was not a function, then we cannot determine the heritage.
    var constructor = prototypeProto.constructor;
    if (typeof constructor !== "function")
        return proto;
    // If we have some kind of self-reference, then we cannot determine the heritage.
    if (constructor === O)
        return proto;
    // we have a pretty good guess at the heritage.
    return constructor;
}
// naive Map shim
function CreateMapPolyfill() {
    var cacheSentinel = {};
    var arraySentinel = [];
    var MapIterator = /** @class */ (function () {
        function MapIterator(keys, values, selector) {
            this._index = 0;
            this._keys = keys;
            this._values = values;
            this._selector = selector;
        }
        MapIterator.prototype["@@iterator"] = function () { return this; };
        MapIterator.prototype[iteratorSymbol] = function () { return this; };
        MapIterator.prototype.next = function () {
            var index = this._index;
            if (index >= 0 && index < this._keys.length) {
                var result = this._selector(this._keys[index], this._values[index]);
                if (index + 1 >= this._keys.length) {
                    this._index = -1;
                    this._keys = arraySentinel;
                    this._values = arraySentinel;
                }
                else {
                    this._index++;
                }
                return { value: result, done: false };
            }
            return { value: undefined, done: true };
        };
        MapIterator.prototype.throw = function (error) {
            if (this._index >= 0) {
                this._index = -1;
                this._keys = arraySentinel;
                this._values = arraySentinel;
            }
            throw error;
        };
        MapIterator.prototype.return = function (value) {
            if (this._index >= 0) {
                this._index = -1;
                this._keys = arraySentinel;
                this._values = arraySentinel;
            }
            return { value: value, done: true };
        };
        return MapIterator;
    }());
    return /** @class */ (function () {
        function Map() {
            this._keys = [];
            this._values = [];
            this._cacheKey = cacheSentinel;
            this._cacheIndex = -2;
        }
        Object.defineProperty(Map.prototype, "size", {
            get: function () { return this._keys.length; },
            enumerable: false,
            configurable: true
        });
        Map.prototype.has = function (key) { return this._find(key, /*insert*/ false) >= 0; };
        Map.prototype.get = function (key) {
            var index = this._find(key, /*insert*/ false);
            return index >= 0 ? this._values[index] : undefined;
        };
        Map.prototype.set = function (key, value) {
            var index = this._find(key, /*insert*/ true);
            this._values[index] = value;
            return this;
        };
        Map.prototype.delete = function (key) {
            var index = this._find(key, /*insert*/ false);
            if (index >= 0) {
                var size = this._keys.length;
                for (var i = index + 1; i < size; i++) {
                    this._keys[i - 1] = this._keys[i];
                    this._values[i - 1] = this._values[i];
                }
                this._keys.length--;
                this._values.length--;
                if (key === this._cacheKey) {
                    this._cacheKey = cacheSentinel;
                    this._cacheIndex = -2;
                }
                return true;
            }
            return false;
        };
        Map.prototype.clear = function () {
            this._keys.length = 0;
            this._values.length = 0;
            this._cacheKey = cacheSentinel;
            this._cacheIndex = -2;
        };
        Map.prototype.keys = function () { return new MapIterator(this._keys, this._values, getKey); };
        Map.prototype.values = function () { return new MapIterator(this._keys, this._values, getValue); };
        Map.prototype.entries = function () { return new MapIterator(this._keys, this._values, getEntry); };
        Map.prototype["@@iterator"] = function () { return this.entries(); };
        Map.prototype[iteratorSymbol] = function () { return this.entries(); };
        Map.prototype._find = function (key, insert) {
            if (this._cacheKey !== key) {
                this._cacheIndex = this._keys.indexOf(this._cacheKey = key);
            }
            if (this._cacheIndex < 0 && insert) {
                this._cacheIndex = this._keys.length;
                this._keys.push(key);
                this._values.push(undefined);
            }
            return this._cacheIndex;
        };
        return Map;
    }());
    function getKey(key, _) {
        return key;
    }
    function getValue(_, value) {
        return value;
    }
    function getEntry(key, value) {
        return [key, value];
    }
}
// naive Set shim
function CreateSetPolyfill() {
    return /** @class */ (function () {
        function Set() {
            this._map = new _Map();
        }
        Object.defineProperty(Set.prototype, "size", {
            get: function () { return this._map.size; },
            enumerable: false,
            configurable: true
        });
        Set.prototype.has = function (value) { return this._map.has(value); };
        Set.prototype.add = function (value) { return this._map.set(value, value), this; };
        Set.prototype.delete = function (value) { return this._map.delete(value); };
        Set.prototype.clear = function () { this._map.clear(); };
        Set.prototype.keys = function () { return this._map.keys(); };
        Set.prototype.values = function () { return this._map.values(); };
        Set.prototype.entries = function () { return this._map.entries(); };
        Set.prototype["@@iterator"] = function () { return this.keys(); };
        Set.prototype[iteratorSymbol] = function () { return this.keys(); };
        return Set;
    }());
}
// naive WeakMap shim
function CreateWeakMapPolyfill() {
    var UUID_SIZE = 16;
    var keys = HashMap.create();
    var rootKey = CreateUniqueKey();
    return /** @class */ (function () {
        function WeakMap() {
            this._key = CreateUniqueKey();
        }
        WeakMap.prototype.has = function (target) {
            var table = GetOrCreateWeakMapTable(target, /*create*/ false);
            return table !== undefined ? HashMap.has(table, this._key) : false;
        };
        WeakMap.prototype.get = function (target) {
            var table = GetOrCreateWeakMapTable(target, /*create*/ false);
            return table !== undefined ? HashMap.get(table, this._key) : undefined;
        };
        WeakMap.prototype.set = function (target, value) {
            var table = GetOrCreateWeakMapTable(target, /*create*/ true);
            table[this._key] = value;
            return this;
        };
        WeakMap.prototype.delete = function (target) {
            var table = GetOrCreateWeakMapTable(target, /*create*/ false);
            return table !== undefined ? delete table[this._key] : false;
        };
        WeakMap.prototype.clear = function () {
            // NOTE: not a real clear, just makes the previous data unreachable
            this._key = CreateUniqueKey();
        };
        return WeakMap;
    }());
    function CreateUniqueKey() {
        var key;
        do
            key = "@@WeakMap@@" + CreateUUID();
        while (HashMap.has(keys, key));
        keys[key] = true;
        return key;
    }
    function GetOrCreateWeakMapTable(target, create) {
        if (!hasOwn.call(target, rootKey)) {
            if (!create)
                return undefined;
            Object.defineProperty(target, rootKey, { value: HashMap.create() });
        }
        return target[rootKey];
    }
    function FillRandomBytes(buffer, size) {
        for (var i = 0; i < size; ++i)
            buffer[i] = Math.random() * 0xff | 0;
        return buffer;
    }
    function GenRandomBytes(size) {
        if (typeof Uint8Array === "function") {
            if (typeof crypto !== "undefined")
                return crypto.getRandomValues(new Uint8Array(size));
            if (typeof msCrypto !== "undefined")
                return msCrypto.getRandomValues(new Uint8Array(size));
            return FillRandomBytes(new Uint8Array(size), size);
        }
        return FillRandomBytes(new Array(size), size);
    }
    function CreateUUID() {
        var data = GenRandomBytes(UUID_SIZE);
        // mark as random - RFC 4122 § 4.4
        data[6] = data[6] & 0x4f | 0x40;
        data[8] = data[8] & 0xbf | 0x80;
        var result = "";
        for (var offset = 0; offset < UUID_SIZE; ++offset) {
            var byte = data[offset];
            if (offset === 4 || offset === 6 || offset === 8)
                result += "-";
            if (byte < 16)
                result += "0";
            result += byte.toString(16).toLowerCase();
        }
        return result;
    }
}
// uses a heuristic used by v8 and chakra to force an object into dictionary mode.
function MakeDictionary(obj) {
    obj.__ = undefined;
    delete obj.__;
    return obj;
}
import 'reflect-metadata';
if (!Reflect.defineMetadata) {
    Reflect.defineMetadata = defineMetadata;
}
if (!Reflect.decorate) {
    Reflect.decorate = decorate;
}
if (!Reflect.deleteMetadata) {
    Reflect.deleteMetadata = deleteMetadata;
}
if (!Reflect.getMetadata) {
    Reflect.getMetadata = getMetadata;
}
if (!Reflect.getMetadataKeys) {
    Reflect.getMetadataKeys = getMetadataKeys;
}
if (!Reflect.getOwnMetadata) {
    Reflect.getOwnMetadata = getOwnMetadata;
}
if (!Reflect.getOwnMetadataKeys) {
    Reflect.getOwnMetadataKeys = getOwnMetadataKeys;
}
if (!Reflect.hasMetadata) {
    Reflect.hasMetadata = hasMetadata;
}
if (!Reflect.hasOwnMetadata) {
    Reflect.hasOwnMetadata = hasOwnMetadata;
}
//
var reflect_defineMetadata = Reflect.defineMetadata;
export { reflect_defineMetadata as defineMetadata };
var reflect_decorate = Reflect.decorate;
export { reflect_decorate as decorate };
var reflect_deleteMetadata = Reflect.deleteMetadata;
export { reflect_deleteMetadata as deleteMetadata };
var reflect_getMetadata = Reflect.getMetadata;
export { reflect_getMetadata as getMetadata };
var reflect_getMetadataKeys = Reflect.getMetadataKeys;
export { reflect_getMetadataKeys as getMetadataKeys };
var reflect_getOwnMetadata = Reflect.getOwnMetadata;
export { reflect_getOwnMetadata as getOwnMetadata };
var reflect_getOwnMetadataKeys = Reflect.getOwnMetadataKeys;
export { reflect_getOwnMetadataKeys as getOwnMetadataKeys };
var reflect_hasMetadata = Reflect.hasMetadata;
export { reflect_hasMetadata as hasMetadata };
var reflect_hasOwnMetadata = Reflect.hasOwnMetadata;
export { reflect_hasOwnMetadata as hasOwnMetadata };
var reflect_apply = Reflect.apply;
export { reflect_apply as apply };
var reflect_construct = Reflect.construct;
export { reflect_construct as construct };
var reflect_defineProperty = Reflect.defineProperty;
export { reflect_defineProperty as defineProperty };
var reflect_deleteProperty = Reflect.deleteProperty;
export { reflect_deleteProperty as deleteProperty };
var reflect_get = Reflect.get;
export { reflect_get as get };
var reflect_getOwnPropertyDescriptor = Reflect.getOwnPropertyDescriptor;
export { reflect_getOwnPropertyDescriptor as getOwnPropertyDescriptor };
var reflect_getPrototypeOf = Reflect.getPrototypeOf;
export { reflect_getPrototypeOf as getPrototypeOf };
var reflect_has = Reflect.has;
export { reflect_has as has };
var reflect_isExtensible = Reflect.isExtensible;
export { reflect_isExtensible as isExtensible };
var reflect_ownKeys = Reflect.ownKeys;
export { reflect_ownKeys as ownKeys };
var reflect_preventExtensions = Reflect.preventExtensions;
export { reflect_preventExtensions as preventExtensions };
var reflect_set = Reflect.set;
export { reflect_set as set };
var reflect_setPrototypeOf = Reflect.setPrototypeOf;
export { reflect_setPrototypeOf as setPrototypeOf };
export default Reflect;
//# sourceMappingURL=data:application/json;base64,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